/**
 * <carousel-progress>
 *
 * Scroll progress bar for the theme's resource-list carousels. The carousel is a
 * plain `overflow-x: scroll` container (`slideshow-slides`), so scroll position
 * drives the bar for arrows, swipes and mouse drags alike.
 *
 * The thumb width is fixed in CSS; this only moves it along the track.
 *
 * Implemented as a custom element rather than an inline script so it also works
 * in sections whose markup is morphed in after a fetch (product recommendations),
 * where inline scripts are not re-executed.
 */

const SCROLLER_SELECTOR = '.resource-list__carousel slideshow-slides';

class CarouselProgress extends HTMLElement {
  /** @type {Element | null} */
  #scroller = null;
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement | null} */
  #indicator = null;
  /** @type {HTMLElement | null} */
  #progress = null;
  /** @type {number | null} */
  #frame = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {MutationObserver | null} */
  #domObserver = null;

  connectedCallback() {
    this.#progress = this.querySelector('.collection-slider__progress');
    this.#track = this.querySelector('.collection-slider__progress-track');
    this.#indicator = this.querySelector('.collection-slider__progress-indicator');

    if (!this.#progress || !this.#track || !this.#indicator) return;

    this.#connect();
  }

  disconnectedCallback() {
    this.#teardown();
  }

  /** The section this bar belongs to, used to scope the scroller lookup. */
  get #root() {
    return this.closest('.section') ?? this.parentElement;
  }

  #connect() {
    const root = this.#root;
    const scroller = root?.querySelector(SCROLLER_SELECTOR);

    // The carousel can arrive after this element does — recommendations render
    // a skeleton first and morph the real markup in later.
    if (!scroller) {
      this.hidden = true;
      this.#waitForScroller(root);
      return;
    }

    this.#scroller = scroller;
    scroller.addEventListener('scroll', this.#schedule, { passive: true });

    if ('ResizeObserver' in window) {
      // Catches both the breakpoint change and the slides settling once their
      // images have loaded.
      this.#resizeObserver = new ResizeObserver(this.#schedule);
      this.#resizeObserver.observe(scroller);
      if (scroller.firstElementChild) this.#resizeObserver.observe(scroller.firstElementChild);
    } else {
      window.addEventListener('resize', this.#schedule, { passive: true });
    }

    this.#update();
  }

  /**
   * @param {Element | null | undefined} root
   */
  #waitForScroller(root) {
    if (!root || this.#domObserver) return;

    this.#domObserver = new MutationObserver(() => {
      if (!root.querySelector(SCROLLER_SELECTOR)) return;

      this.#domObserver?.disconnect();
      this.#domObserver = null;
      this.#connect();
    });

    this.#domObserver.observe(root, { childList: true, subtree: true });
  }

  #schedule = () => {
    if (this.#frame == null) this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    this.#frame = null;

    const scroller = this.#scroller;
    const track = this.#track;
    const indicator = this.#indicator;
    if (!scroller || !track || !indicator) return;

    const scrollable = scroller.scrollWidth - scroller.clientWidth;

    // Nothing to scroll, or the carousel is hidden at this breakpoint.
    if (scrollable <= 1) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    // scrollLeft is negative in RTL, so normalise it.
    const progressRatio = Math.min(1, Math.abs(scroller.scrollLeft) / scrollable);
    // The thumb is a fixed width in CSS, so read it back rather than hardcoding
    // it here — it can only travel the remaining track.
    const travel = Math.max(0, 1 - indicator.offsetWidth / track.clientWidth);

    indicator.style.insetInlineStart = `${(progressRatio * travel * 100).toFixed(4)}%`;
    if (this.#progress) this.#progress.dataset.progressInitialized = 'true';
  };

  #teardown() {
    if (this.#frame != null) cancelAnimationFrame(this.#frame);
    this.#frame = null;

    this.#scroller?.removeEventListener('scroll', this.#schedule);
    this.#scroller = null;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    this.#domObserver?.disconnect();
    this.#domObserver = null;

    window.removeEventListener('resize', this.#schedule);
  }
}

if (!customElements.get('carousel-progress')) {
  customElements.define('carousel-progress', CarouselProgress);
}
